import { Inject, Injectable } from '@angular/core';
import {BehaviorSubject, catchError, filter, interval, of, startWith, Subject, switchMap, tap} from 'rxjs';
import { BooxSessionService, BooxApiService } from 'lib-boox';
import { HERO_FLOW_SERVICE } from '../token';

export interface IHeroNotification {
  id: string;
  title: string;
  content: string;
  link?: string;
}

export interface IHeroNotificationResponse {
  has_card: number;
  verify_card: number;
}

@Injectable({
  providedIn: 'root'
})
export class HeroNotificationService {
  // Use more specific types for BehaviorSubject
  private readonly notifications$ = new BehaviorSubject<IHeroNotification[]>([]);
  private readonly notificationCount$ = new BehaviorSubject<number>(0);
  private readonly newNotification$ = new Subject<boolean>();
  private readonly notificationVeriApp$ = new Subject<IHeroNotification>();

  // Public observables (unchanged)
  public readonly notifications = this.notifications$.asObservable();
  public readonly notificationCount = this.notificationCount$.asObservable();
  public readonly newNotification = this.newNotification$.asObservable();
  public readonly notificationVeriApp = this.notificationVeriApp$.asObservable();

  // Constants for configuration
  private readonly POLLING_INTERVAL = 10_000; // 10 seconds
  private readonly NOTIFICATION_TYPES = {
    NO_CARD: 'no_card',
    VERIFY_CARD: 'verify_card'
  };

  constructor(
    private readonly session: BooxSessionService,
    private readonly boox: BooxApiService,
    @Inject(HERO_FLOW_SERVICE) private readonly flowService: string,

  ) {
    // Combine session and polling into a single subscription
    this.session.$session.pipe(
      filter(session => !!session),
      switchMap(() => interval(this.POLLING_INTERVAL).pipe(
        startWith(0), // Trigger immediately on session change
        switchMap(() => this.loadNotifications())
      )),
      catchError(err => {
        console.error('Notification stream error:', err);
        return of(null);
      })
    ).subscribe();
  }

  private loadNotifications() {
    return this.boox.post<IHeroNotificationResponse>(`${this.flowService}/_proc/get_notifications`, {}).pipe(
      tap(response => response && this.handleNotificationResponse(response)),
      catchError(err => {
        console.error('Failed to fetch notifications:', err);
        return of(null);
      })
    );
  }

  private handleNotificationResponse(response: IHeroNotificationResponse): void {
    const currentNotifications = this.notifications$.getValue();
    const currentCount = this.notificationCount$.getValue();

    const notifications: IHeroNotification[] = [];
    let count = 0;

    // Handle no_card notification
    if (!response.has_card) {
      notifications.push({
        id: this.NOTIFICATION_TYPES.NO_CARD,
        title: 'Keine Card',
        content: 'Du hast noch keine Card, bitte erstelle eine.',
        link: '/profile'
      });
      count += 1;
    }

    // Handle verify_card notification
    if (response.verify_card > 0) {
      const verifyCard: IHeroNotification = {
        id: this.NOTIFICATION_TYPES.VERIFY_CARD,
        title: 'Card nicht verifiziert',
        content: `Du hast <span class="fs-5">${response.verify_card}</span> Card${response.verify_card > 1 ? 's' : ''} Deiner Mitarbeiter zu verifizieren.`,
        link: '/app/veri/changes'
      };
      notifications.push(verifyCard);
      count += response.verify_card;
      this.notificationVeriApp$.next(verifyCard);
    }

    // Optimize change detection
    const hasChanges = this.hasNotificationChanges(currentNotifications, notifications, currentCount, count);
    if (hasChanges) {
      this.notifications$.next(notifications);
      this.notificationCount$.next(count);
      this.newNotification$.next(true);
    } else {
      this.newNotification$.next(false);
    }
  }

  private hasNotificationChanges(
    current: IHeroNotification[],
    next: IHeroNotification[],
    currentCount: number,
    nextCount: number
  ): boolean {
    if (currentCount !== nextCount || current.length !== next.length) {
      return true;
    }

    // Deep comparison of notifications
    return !current.every(curr =>
      next.some(next =>
        next.id === curr.id &&
        next.title === curr.title &&
        next.content === curr.content &&
        next.link === curr.link
      )
    );
  }

  // Getter methods (unchanged but with readonly)
  public get currentNotifications(): Readonly<IHeroNotification[]> {
    return this.notifications$.getValue();
  }

  public get currentNotificationCount(): number {
    return this.notificationCount$.getValue();
  }

  // Cleanup method
  public ngOnDestroy(): void {
    this.notifications$.complete();
    this.notificationCount$.complete();
    this.newNotification$.complete();
    this.notificationVeriApp$.complete();
  }
}
